import { useCallback, useEffect } from "react";
import { TaskStatus } from "tm-shared/types";

import { useAppDispatch } from "@/hooks/redux";
import { setAssortmentMatrixCompletion } from "@/store/slices/progress";
import { isSubtaskCompleted } from "@/utils/business/isSubtaskCompleted";

export const useAssortmentMatrixCompletion = <
    T extends { status: TaskStatus; comment?: string },
>({
    taskId,
    subtaskId,
    expectedCount,
    payloadsMap,
}: {
    taskId: string;
    subtaskId: string;
    expectedCount: number;
    payloadsMap: Record<string, T> | null;
}) => {
    const dispatch = useAppDispatch();

    const setCompletion = useCallback(
        (value: boolean) => {
            dispatch(
                setAssortmentMatrixCompletion({
                    taskId,
                    subtaskId,
                    value,
                }),
            );
        },
        [dispatch, taskId, subtaskId],
    );

    useEffect(() => {
        if (!payloadsMap) {
            setCompletion(false);
            return;
        }

        const actualCount = Object.keys(payloadsMap).length;
        if (expectedCount !== actualCount) {
            setCompletion(false);
            return;
        }

        const allCompleted =
            Object.values(payloadsMap).every(isSubtaskCompleted);

        setCompletion(allCompleted);
    }, [payloadsMap, expectedCount, setCompletion]);
};
